
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
}

export default function SEOHead({ title, description, keywords }: SEOHeadProps) {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  
  const currentLanguage = i18n.language;
  const baseUrl = window.location.origin;
  const currentPath = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  
  // Default SEO content based on page
  const getPageSEO = () => {
    const path = currentPath.toLowerCase();
    
    if (path === '/' || path === '/home') {
      return {
        title: t('seo.home.title'),
        description: t('seo.home.description'),
        keywords: t('seo.home.keywords')
      };
    } else if (path.includes('/music')) {
      return {
        title: t('seo.music.title'),
        description: t('seo.music.description'),
        keywords: t('seo.music.keywords')
      };
    } else if (path.includes('/videos')) {
      return {
        title: t('seo.videos.title'),
        description: t('seo.videos.description'),
        keywords: t('seo.videos.keywords')
      };
    } else if (path.includes('/about')) {
      return {
        title: t('seo.about.title'),
        description: t('seo.about.description'),
        keywords: t('seo.about.keywords')
      };
    } else if (path.includes('/contact')) {
      return {
        title: t('seo.contact.title'),
        description: t('seo.contact.description'),
        keywords: t('seo.contact.keywords')
      };
    } else {
      return {
        title: t('seo.title'),
        description: t('seo.description'),
        keywords: t('seo.keywords')
      };
    }
  };

  const pageSEO = getPageSEO();
  const seoTitle = title || pageSEO.title;
  const seoDescription = description || pageSEO.description;
  const seoKeywords = keywords || pageSEO.keywords;

  useEffect(() => {
    // Update document title
    document.title = seoTitle;
    
    // Update HTML lang attribute
    document.documentElement.lang = currentLanguage;
    
    // Update meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const attribute = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attribute, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Update standard meta tags
    updateMetaTag('description', seoDescription);
    updateMetaTag('keywords', seoKeywords);
    updateMetaTag('author', 'Jean-Claude Bastos');
    updateMetaTag('robots', 'index, follow');
    
    // Update Open Graph tags
    updateMetaTag('og:type', 'website', true);
    updateMetaTag('og:title', seoTitle, true);
    updateMetaTag('og:description', seoDescription, true);
    updateMetaTag('og:url', `${baseUrl}${currentPath}`, true);
    updateMetaTag('og:site_name', 'Jean-Claude Bastos - Official Website', true);
    updateMetaTag('og:image', `${baseUrl}/og-image.jpg`, true);
    updateMetaTag('og:image:width', '1200', true);
    updateMetaTag('og:image:height', '630', true);
    updateMetaTag('og:image:alt', 'Jean-Claude Bastos - UK Electronic Music Producer', true);
    
    // Language-specific Open Graph locale
    const ogLocale = currentLanguage === 'en' ? 'en_US' : 
                     currentLanguage === 'de' ? 'de_DE' : 
                     currentLanguage === 'it' ? 'it_IT' : 'en_US';
    updateMetaTag('og:locale', ogLocale, true);
    
    // Add alternate locales
    const alternateLocales = ['en_US', 'de_DE', 'it_IT'].filter(locale => locale !== ogLocale);
    alternateLocales.forEach((locale, index) => {
      updateMetaTag(`og:locale:alternate`, locale, true);
    });
    
    // Update Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:site', '@jeanclaudebastos', true);
    updateMetaTag('twitter:creator', '@jeanclaudebastos', true);
    updateMetaTag('twitter:title', seoTitle, true);
    updateMetaTag('twitter:description', seoDescription, true);
    updateMetaTag('twitter:image', `${baseUrl}/og-image.jpg`, true);
    updateMetaTag('twitter:image:alt', 'Jean-Claude Bastos - UK Electronic Music Producer', true);
    
    // Add structured data (JSON-LD)
    const removeExistingJsonLd = () => {
      const existingJsonLd = document.querySelector('script[type="application/ld+json"]');
      if (existingJsonLd) {
        existingJsonLd.remove();
      }
    };
    
    removeExistingJsonLd();
    
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Jean-Claude Bastos",
      "alternateName": ["DJ Jean-Claude Bastos", "Jean Claude Bastos", "JC Bastos"],
      "description": seoDescription,
      "url": `${baseUrl}${currentPath}`,
      "image": `${baseUrl}/og-image.jpg`,
      "sameAs": [
        "https://www.jeanclaudebastosmusic.com",
        "https://soundcloud.com/dj-jean-claude-bastos",
        "https://open.spotify.com/artist/7MWynFxiCsINwkfZHunLEU",
        "https://www.youtube.com/@DJJeanClaudeBastos",
        "https://music.apple.com/artist/jean-claude-bastos",
        "https://www.instagram.com/jeanclaudebastos",
        "https://twitter.com/jeanclaudebastos"
      ],
      "jobTitle": "Electronic Music Producer",
      "hasOccupation": {
        "@type": "Occupation",
        "name": "Electronic Music Producer",
        "occupationLocation": {
          "@type": "Country",
          "name": "United Kingdom"
        }
      },
      "genre": ["Progressive House", "Melodic Techno", "Electronic Dance Music", "EDM"],
      "nationality": "British",
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "GB"
      },
      "knowsAbout": [
        "Electronic Music Production",
        "Progressive House",
        "Melodic Techno",
        "DJ Performance",
        "Music Composition",
        "Sound Design"
      ],
      "alumniOf": {
        "@type": "Organization",
        "name": "UK Electronic Music Scene"
      },
      "award": [
        "Break the Ground - Progressive House Hit",
        "Running Free - Summer Electronic Anthem",
        "When We Loved - Melodic Techno Journey"
      ],
      "owns": {
        "@type": "MusicGroup",
        "name": "Jean-Claude Bastos Music",
        "genre": ["Progressive House", "Melodic Techno", "Electronic Dance Music"]
      }
    };

    // Add additional structured data for music-specific pages
    if (currentPath.includes('/music')) {
      const musicStructuredData = {
        "@context": "https://schema.org",
        "@type": "MusicAlbum",
        "name": "Jean-Claude Bastos - Latest Releases",
        "albumProductionType": "http://schema.org/StudioAlbum",
        "byArtist": {
          "@type": "Person",
          "name": "Jean-Claude Bastos"
        },
        "genre": ["Progressive House", "Melodic Techno", "Electronic Dance Music"],
        "datePublished": "2024",
        "track": [
          {
            "@type": "MusicRecording",
            "name": "Break the Ground",
            "byArtist": "Jean-Claude Bastos",
            "genre": "Progressive House",
            "duration": "PT4M30S"
          },
          {
            "@type": "MusicRecording", 
            "name": "Running Free",
            "byArtist": "Jean-Claude Bastos",
            "genre": "Progressive House",
            "duration": "PT5M15S"
          },
          {
            "@type": "MusicRecording",
            "name": "When We Loved", 
            "byArtist": "Jean-Claude Bastos",
            "genre": "Melodic Techno",
            "duration": "PT6M45S"
          }
        ]
      };

      // Add music structured data as separate script
      const musicJsonLdScript = document.createElement('script');
      musicJsonLdScript.type = 'application/ld+json';
      musicJsonLdScript.textContent = JSON.stringify(musicStructuredData);
      document.head.appendChild(musicJsonLdScript);
    }
    
    const jsonLdScript = document.createElement('script');
    jsonLdScript.type = 'application/ld+json';
    jsonLdScript.textContent = JSON.stringify(structuredData);
    document.head.appendChild(jsonLdScript);

    // Add breadcrumb structured data
    const breadcrumbData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Jean-Claude Bastos",
          "item": baseUrl
        }
      ]
    };

    // Add current page to breadcrumb
    if (currentPath !== '/' && currentPath !== '/home') {
      const pageName = currentPath.replace('/', '').charAt(0).toUpperCase() + currentPath.replace('/', '').slice(1);
      breadcrumbData.itemListElement.push({
        "@type": "ListItem",
        "position": 2,
        "name": `${pageName} - Jean-Claude Bastos`,
        "item": `${baseUrl}${currentPath}`
      });
    }
    
    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.textContent = JSON.stringify(breadcrumbData);
    document.head.appendChild(breadcrumbScript);
    
    // Remove existing hreflang links
    const existingHreflangs = document.querySelectorAll('link[hreflang]');
    existingHreflangs.forEach(link => link.remove());
    
    // Add hreflang links for multilingual SEO with proper URL structure
    const languages = [
      { code: 'en', locale: 'en-US' },
      { code: 'de', locale: 'de-DE' },
      { code: 'it', locale: 'it-IT' }
    ];
    
    languages.forEach(lang => {
      const link = document.createElement('link');
      link.rel = 'alternate';
      link.hreflang = lang.code;
      
      // Create proper URL with language parameter
      const langParams = new URLSearchParams();
      langParams.set('lang', lang.code);
      link.href = `${baseUrl}${currentPath}?${langParams.toString()}`;
      
      document.head.appendChild(link);
    });
    
    // Add x-default hreflang (points to English version)
    const defaultLink = document.createElement('link');
    defaultLink.rel = 'alternate';
    defaultLink.hreflang = 'x-default';
    const defaultParams = new URLSearchParams();
    defaultParams.set('lang', 'en');
    defaultLink.href = `${baseUrl}${currentPath}?${defaultParams.toString()}`;
    document.head.appendChild(defaultLink);
    
    // Update canonical link to include current language
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    
    // Set canonical URL with current language parameter
    const canonicalParams = new URLSearchParams();
    canonicalParams.set('lang', currentLanguage);
    canonical.href = `${baseUrl}${currentPath}?${canonicalParams.toString()}`;
    
    // Add additional SEO meta tags
    updateMetaTag('theme-color', '#00BCD4');
    updateMetaTag('msapplication-TileColor', '#00BCD4');
    updateMetaTag('application-name', 'Jean-Claude Bastos');
    updateMetaTag('apple-mobile-web-app-title', 'Jean-Claude Bastos');
    updateMetaTag('apple-mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'black-translucent');
    
  }, [currentLanguage, seoTitle, seoDescription, seoKeywords, currentPath, baseUrl, location.search]);

  return null;
}
